import { ObjectDB, ObjectId } from "@novorender/data-js-api";
import { HierarcicalObjectReference } from "@novorender/data-js-api";
import pMap from "p-map";

import { AsyncTreeChangeResult, Node, Tree } from "components/objectTree/types";
import { ensureExpandedTo, getParentPaths } from "components/objectTree/utils";
import type { ObjectDataAccess } from "dataAccess";
import { telemetry } from "telemetry";
import { NodeType } from "types/misc";
import { getFilePathFromObjectPath, getObjectNameFromPath, isLeafNode, isPdfOrDrawingObject } from "utils/objectData";
import { iterateAsync } from "utils/search";

import { ModelTreeFilter, ModelTreeFilterObjects, ModelTreeFilterType, ModelTreeNodeData } from "./types";

/**
 * Natural, numeric-aware ordering for sibling paths so that objects of the same name
 * disambiguated by an index (e.g. "Beam (2)" and "Beam (10)") sort by their index value
 * rather than lexicographically (which would order "(10)" before "(2)").
 */
const childPathCollator = new Intl.Collator(undefined, { numeric: true });

/**
 * The identity {@link ObjectDataAccess.getChildren} lists by, for a path in the tree.
 *
 * Three answers, not two:
 *
 * - `undefined` — the root tier. It is what `getChildren` takes for the top of the tree,
 *   and the root node's own object is a placeholder, so a backend keyed by id would list
 *   nothing for it. The root never reads `fallback`.
 * - an object — list by it.
 * - `null` — no object is known for the path. {@link resolveParentRef} looks it up.
 *
 * @param fallback Objects to fall back to for paths with no node yet, keyed by path.
 */
function parentRef(
    tree: Tree<ModelTreeNodeData>,
    path: string,
    fallback?: Map<string, HierarcicalObjectReference>,
): HierarcicalObjectReference | undefined | null {
    if (path === "") {
        return undefined;
    }
    const object = tree[path]?.data.object ?? fallback?.get(path);
    return object ?? null;
}

/**
 * {@link parentRef}, with its `null` resolved against the backend by path.
 *
 * Still `null` when the path resolves to nothing — then there really is nothing to list.
 */
async function resolveParentRef(
    tree: Tree<ModelTreeNodeData>,
    path: string,
    dataAccess: ObjectDataAccess,
    abortSignal: AbortSignal,
): Promise<HierarcicalObjectReference | undefined | null> {
    const parent = parentRef(tree, path);
    if (parent !== null) {
        return parent;
    }
    return (await dataAccess.getObjectAtPath(path, abortSignal)) ?? null;
}

/** Clears `loading`, and `hasMoreChildren` — nothing further can be listed for the path. */
function stopListing(path: string) {
    return (tree: Tree<ModelTreeNodeData>) => {
        const node = tree[path];
        return node ? { ...tree, [path]: { ...node, loading: false, hasMoreChildren: false } } : tree;
    };
}

export function expandNode({
    tree,
    path,
    dataAccess,
    abortSignal,
    fileTypes,
    skipPdfFiles,
    knownPdfObjectIds,
}: {
    tree: Tree<ModelTreeNodeData>;
    path: string;
    dataAccess: ObjectDataAccess;
    abortSignal: AbortSignal;
    fileTypes?: ModelTreeFilterObjects[];
    skipPdfFiles?: boolean;
    knownPdfObjectIds?: Pick<Set<ObjectId>, "has">;
}): AsyncTreeChangeResult<ModelTreeNodeData> {
    const node = tree[path];
    if (!node || node.expanded || node.isLeaf) {
        return { tree };
    }

    if (node.children) {
        // Already loaded - just expand.
        const newTree: Tree<ModelTreeNodeData> = { ...tree, [path]: { ...node, expanded: true } };
        return { tree: newTree };
    }

    return {
        tree: { ...tree, [path]: { ...node, loading: true } },
        rollback: (tree) => ({ ...tree, [path]: { ...node, loading: false } }),
        load: async () => {
            const parent = await resolveParentRef(tree, path, dataAccess, abortSignal);
            if (parent === null) {
                return (tree, relevant) => {
                    const stopped = stopListing(path)(tree);
                    const node = stopped[path];
                    return node && relevant ? { ...stopped, [path]: { ...node, expanded: true } } : stopped;
                };
            }

            const { descendants, iterator } = await loadChildren({
                dataAccess,
                parent,
                tree,
                fileTypes: fileTypes,
                abortSignal,
                skipPdfFiles,
                knownPdfObjectIds,
            });

            return (tree, relevant) => {
                const node = tree[path];
                if (!node) {
                    return tree;
                }

                const newTree = { ...tree };
                const newNode = appendDescendants({ tree: newTree, node, descendants, iterator });
                newNode.loading = false;
                if (relevant) {
                    newNode.expanded = true;
                }
                newTree[path] = newNode;

                return newTree;
            };
        },
    };
}

export function makeNode(obj: HierarcicalObjectReference): Node<ModelTreeNodeData> {
    const name = getObjectNameFromPath(obj.path);

    const node: Node<ModelTreeNodeData> = {
        expanded: false,
        isLeaf: isLeafNode(obj),
        name,
        path: obj.path,
        data: { object: obj },
        loading: false,
    };

    if (node.isLeaf) {
        const m = name.match(/^(.+)\s+\(?\d+\)?$/);
        if (m) {
            node.groupKey = m[1];
            node.groupCollapsed ??= true;
        }
    }

    return node;
}

export function collapseNode({ tree, path }: { tree: Tree<ModelTreeNodeData>; path: string }) {
    const node = tree[path];
    if (!node) {
        return tree;
    }

    return { ...tree, [path]: { ...node, expanded: false } };
}

export function selectNode({
    tree,
    mainObject,
    dataAccess,
    abortSignal,
    fileTypes,
    skipPdfFiles,
    knownPdfObjectIds,
}: {
    tree: Tree<ModelTreeNodeData>;
    mainObject: HierarcicalObjectReference;
    dataAccess: ObjectDataAccess;
    abortSignal: AbortSignal;
    fileTypes?: ModelTreeFilterObjects[];
    skipPdfFiles?: boolean;
    knownPdfObjectIds?: Pick<Set<ObjectId>, "has">;
}): {
    /**
     * Either fully updated tree if no async operations needed, or tree with `loading` flag set.
     */
    tree: Tree<ModelTreeNodeData>;
    rollback?: (tree: Tree<ModelTreeNodeData>) => Tree<ModelTreeNodeData>;
    load?: () => Promise<{
        apply: (tree: Tree<ModelTreeNodeData>, relevant: boolean) => Tree<ModelTreeNodeData>;
        loadUpTo?: { parentPath: string; loadUpToPath: string }[];
    }>;
} {
    // if already in the tree - just expand all parents
    if (tree[mainObject.path]) {
        return { tree: ensureExpandedTo(tree, mainObject.path) };
    }

    // if filtered - check that this object can be visible at all
    if (fileTypes) {
        const satisfiesFilter = fileTypes.some(({ objects }) =>
            objects.some((obj) => mainObject.path.startsWith(obj.path) || obj.path.startsWith(mainObject.path)),
        );
        if (!satisfiesFilter) {
            return { tree: tree };
        }
    }

    const parentPaths = [...getParentPaths(mainObject.path)];
    const newTree = { ...tree };

    for (const path of parentPaths) {
        const node = newTree[path];
        if (node && !node.expanded) {
            newTree[path] = node.children ? { ...node, expanded: true } : { ...node, loading: true };
        }
    }

    const loadCriticalPath = async () => {
        const alreadyLoaded = parentPaths.every((path) => newTree[path]);
        if (alreadyLoaded) {
            return parentPaths.map((path) => newTree[path].data.object);
        }

        // Root is synthesized, and `getAncestors` never returns it. Ancestors the walk
        // could not produce are absent rather than guessed — appendDescendants refuses
        // non-direct children, so a guess would hoist a subtree under the wrong parent
        // and render it in two places.
        return [tree[""].data.object, ...(await dataAccess.getAncestors(mainObject, abortSignal))];
    };

    const load = async () => {
        const loadUpToCandidates: { parentPath: string; loadUpToPath: string }[] = [];

        const loadLevel = async (parentPath: string, i: number, parent: HierarcicalObjectReference | undefined) => {
            const node = tree[parentPath];
            const childPath = i === parentPaths.length - 1 ? mainObject.path : parentPaths[i + 1];
            const { descendants, iterator } = node?.children
                ? { descendants: [] as HierarcicalObjectReference[], iterator: node.data.childIterator }
                : await loadChildren({
                      dataAccess,
                      parent,
                      tree,
                      fileTypes: fileTypes,
                      abortSignal,
                      skipPdfFiles,
                      knownPdfObjectIds,
                  });

            if (!node?.children?.includes(childPath) && !descendants.some((d) => d.path === childPath)) {
                loadUpToCandidates.push({
                    parentPath: parentPath,
                    loadUpToPath: childPath,
                });
            }

            return { path: parentPath, descendants, iterator };
        };

        // The ancestor walk comes first because `getChildren` lists by object, and for a
        // level with no node yet the walk is the only thing that can supply one.
        const criticalPath = await loadCriticalPath();
        const resolved = new Map(criticalPath.map((obj) => [obj.path, obj]));

        // So a level is listable only where a real object exists: the root, one already
        // in the tree, or one the walk resolved. A level with none is skipped rather than
        // listed by path — apply() creates no node for it, so its children would have
        // nothing to attach to.
        const listable = parentPaths.flatMap((path, i) => {
            const parent = parentRef(tree, path, resolved);
            return parent === null ? [] : [{ path, i, parent }];
        });
        const descendantLists = await pMap(listable, ({ path, i, parent }) => loadLevel(path, i, parent), {
            concurrency: 4,
            signal: abortSignal,
        });

        const apply = (tree: Tree<ModelTreeNodeData>, relevant: boolean) => {
            const newTree: Tree<ModelTreeNodeData> = { ...tree };
            criticalPath.forEach((obj, pathIndex) => {
                let node = newTree[obj.path];
                if (!node) {
                    node = {
                        expanded: true,
                        name: getObjectNameFromPath(obj.path),
                        path: obj.path,
                        isLeaf: isLeafNode(obj),
                        data: { object: obj },
                        loading: false,
                    };
                } else {
                    node = { ...node };
                }
                if (relevant) {
                    node.expanded = true;
                    node.groupCollapsed = false;
                }
                node.loading = false;
                const { descendants, iterator } = descendantLists.find((d) => d.path === obj.path) ?? {
                    descendants: [] as HierarcicalObjectReference[],
                    iterator: node.data.childIterator,
                };

                // If critical path child is still not loaded - pick it from the critical path.
                const childCriticalPathNode =
                    pathIndex === criticalPath.length - 1 ? mainObject : criticalPath[pathIndex + 1];
                if (
                    !descendants.some((d) => d.path === childCriticalPathNode.path) &&
                    !node.children?.includes(childCriticalPathNode.path)
                ) {
                    descendants.push(childCriticalPathNode);
                }

                if (descendants.length > 0) {
                    node = appendDescendants({
                        tree: newTree,
                        node,
                        descendants: descendants,
                        iterator: iterator,
                    });
                }

                newTree[obj.path] = node;
            });

            // Ensure leaf is not groupCollapsed
            const mainObjectNode = newTree[mainObject.path];
            if (mainObjectNode && mainObjectNode.groupCollapsed) {
                newTree[mainObject.path] = { ...mainObjectNode, groupCollapsed: false };
            }

            return newTree;
        };

        return { apply, loadUpTo: loadUpToCandidates };
    };

    return {
        tree: newTree,
        rollback: (tree) => {
            let newTree = tree;
            for (const path of parentPaths) {
                const node = newTree[path];
                if (node && node.loading) {
                    if (newTree === tree) {
                        newTree = { ...tree };
                    }
                    newTree[path] = { ...node, loading: false };
                }
            }
            return newTree;
        },
        load,
    };
}

export function filterOutObjectsNotOnPath(tree: Tree<ModelTreeNodeData>, paths: string[]) {
    const pathSet = new Set<string>();
    for (const concretePath of paths) {
        for (const path of getParentPaths(concretePath, false)) {
            pathSet.add(path);
        }
    }

    const newTree: Tree<ModelTreeNodeData> = {};
    Object.values(tree).forEach((node) => {
        if (pathSet.has(node.path)) {
            newTree[node.path] = node;
        }
    });

    Object.values(newTree).forEach((node) => {
        if (node.children) {
            node = { ...node, children: node.children.filter((child) => newTree[child]) };
            newTree[node.path] = node;
        }
    });

    return newTree;
}

export async function loadUpTo({
    dataAccess,
    tree,
    path,
    loadUpToPath,
    fileTypes,
    abortSignal,
    skipPdfFiles,
    knownPdfObjectIds,
}: {
    dataAccess: ObjectDataAccess;
    tree: Tree<ModelTreeNodeData>;
    path: string;
    loadUpToPath: string;
    fileTypes?: ModelTreeFilterObjects[];
    abortSignal: AbortSignal;
    skipPdfFiles?: boolean;
    knownPdfObjectIds?: Pick<Set<ObjectId>, "has">;
}) {
    const parent = await resolveParentRef(tree, path, dataAccess, abortSignal);
    if (parent === null) {
        // Nothing to list by, so the patch below would be a no-op anyway.
        return (tree: Tree<ModelTreeNodeData>) => tree;
    }

    const { descendants, iterator } = await loadChildren({
        dataAccess,
        parent,
        tree,
        fileTypes: fileTypes,
        abortSignal,
        ensurePathFound: loadUpToPath,
        skipPdfFiles,
        knownPdfObjectIds,
    });

    return (tree: Tree<ModelTreeNodeData>) => {
        if (descendants.length === 0) {
            return tree;
        }

        const node = tree[path];
        if (!node) {
            return tree;
        }

        const newTree = { ...tree };
        newTree[path] = appendDescendants({ tree: newTree, node: newTree[path], descendants, iterator });
        return newTree;
    };
}

export async function loadFilesWithType({
    db,
    abortSignal,
    fileTypes,
}: {
    db: ObjectDB;
    abortSignal: AbortSignal;
    fileTypes: ModelTreeFilter[];
}) {
    const hasTerrain = fileTypes.some((f) => f.type === ModelTreeFilterType.Terrain);
    const parserTypes = fileTypes.filter((f) => f.type !== ModelTreeFilterType.Terrain).map((f) => f.parserType);

    const [[terrainObjects], [genericTypeObjects]] = await Promise.all([
        hasTerrain
            ? iterateAsync({
                  iterator: db.search(
                      {
                          searchPattern: [
                              {
                                  property: "id",
                                  value: Array.from({ length: 100 }, (_, i) => i.toString()),
                                  exact: true,
                              },
                          ],
                          full: true,
                      },
                      abortSignal,
                  ),
                  count: 100,
                  abortSignal: abortSignal,
              })
            : [[]],
        parserTypes.length
            ? iterateAsync({
                  iterator: db.search(
                      { searchPattern: [{ property: "Parser/Type", value: parserTypes, exact: true }], full: true },
                      abortSignal,
                  ),
                  count: 3000,
                  abortSignal: abortSignal,
              })
            : [[]],
    ]);
    const objectsByFileType = new Map<string, HierarcicalObjectReference[]>();

    if (hasTerrain) {
        objectsByFileType.set(filterTypeToStr({ type: ModelTreeFilterType.Terrain }), terrainObjects);
    }

    for (const obj of genericTypeObjects) {
        const fileName = getFilePathFromObjectPath(obj.path);
        if (!fileName) {
            continue;
        }
        const parserType = (await obj.loadMetaData()).properties.find((p) => p[0] === "Parser/Type")?.[1];
        if (!parserType) {
            continue;
        }
        const key = filterTypeToStr({ type: ModelTreeFilterType.ParserType, parserType });
        let parserTypeObjects = objectsByFileType.get(key);
        if (!parserTypeObjects) {
            parserTypeObjects = [];
            objectsByFileType.set(key, parserTypeObjects);
        }
        parserTypeObjects.push(obj);
    }
    return objectsByFileType;
}

export function* getDescendantsFilteredByParserTypeInfo(path: string, fileTypeFilter: ModelTreeFilterObjects[]) {
    for (const { objects } of fileTypeFilter) {
        for (const obj of objects) {
            if (obj.path.startsWith(path)) {
                const nextSlash = obj.path.indexOf("/", path.length + 1);
                yield nextSlash !== -1 ? obj.path.slice(0, nextSlash) : obj.path;
            }
        }
    }
}

export function collapseAllBelowFileLevel(tree: Tree<ModelTreeNodeData>) {
    const treeMap = { ...tree };
    for (const node of Object.values(treeMap)) {
        const filePath = getFilePathFromObjectPath(node.path);
        if (filePath && filePath.length <= node.path.length && node.expanded) {
            treeMap[node.path] = { ...node, expanded: false };
        }
    }
    return treeMap;
}

export async function getObjectParentFile({
    dataAccess,
    mainObject,
    fileTypes,
    tree,
    abortSignal,
}: {
    dataAccess: ObjectDataAccess;
    mainObject: HierarcicalObjectReference;
    fileTypes?: ModelTreeFilterObjects[];
    tree?: Tree<ModelTreeNodeData>;
    abortSignal: AbortSignal;
}): Promise<HierarcicalObjectReference | undefined> {
    if (fileTypes) {
        for (const { objects } of fileTypes) {
            for (const obj of objects) {
                if (mainObject.path.startsWith(obj.path)) {
                    return obj;
                }
            }
        }
    }

    const filePath = getFilePathFromObjectPath(mainObject.path);
    if (!filePath) {
        return;
    }

    if (tree) {
        const node = tree[filePath];
        if (node) {
            return node.data.object;
        }
    }

    return dataAccess.getObjectAtPath(filePath, abortSignal);
}

export function loadMore({
    dataAccess,
    tree,
    path,
    fileTypes,
    abortSignal,
    skipPdfFiles,
    knownPdfObjectIds,
}: {
    dataAccess: ObjectDataAccess;
    tree: Tree<ModelTreeNodeData>;
    path: string;
    fileTypes?: ModelTreeFilterObjects[];
    abortSignal: AbortSignal;
    skipPdfFiles?: boolean;
    knownPdfObjectIds?: Pick<Set<ObjectId>, "has">;
}): AsyncTreeChangeResult<ModelTreeNodeData> {
    const node = tree[path];
    if (!node || !node.hasMoreChildren || !node.children || node.loading) {
        return { tree };
    }

    return {
        tree: { ...tree, [path]: { ...node, loading: true } },
        rollback: (tree) => ({ ...tree, [path]: { ...node, loading: false } }),
        load: async () => {
            const parent = await resolveParentRef(tree, path, dataAccess, abortSignal);
            if (parent === null) {
                return stopListing(path);
            }

            const { descendants, iterator } = await loadChildren({
                dataAccess,
                parent,
                tree,
                fileTypes,
                abortSignal,
                skipPdfFiles,
                knownPdfObjectIds,
            });

            return (tree, _relevant) => {
                const node = tree[path];
                if (!node) {
                    return tree;
                }

                const newNode = appendDescendants({ tree, node, descendants, iterator });
                newNode.loading = false;
                return { ...tree, [path]: newNode };
            };
        },
    };
}

function appendDescendants({
    tree,
    node,
    descendants,
    iterator,
}: {
    tree: Tree<ModelTreeNodeData>;
    node: Node<ModelTreeNodeData>;
    descendants: HierarcicalObjectReference[];
    iterator?: AsyncIterableIterator<HierarcicalObjectReference>;
}) {
    for (const child of descendants) {
        let node = tree[child.path];
        if (!node) {
            node = makeNode(child);
            tree[child.path] = node;
        }
    }

    // Dedupe by path against BOTH the existing children and the incoming batch itself. Some
    // models (e.g. certain IFC exports) contain several sibling records with the exact same
    // path; the tree is keyed by path, so they all map to one node. Letting the same path
    // into `children` more than once makes every tree traversal (makeNodeList, counters,
    // descendant collection) expand that shared subtree once per duplicate — exponential
    // (3^depth) when duplicates nest, which exhausts renderer memory and crashes the tab.
    // A node must never list itself as a child: that creates a cycle which turns every
    // traversal into an infinite loop (renderer OOM).
    const childPaths = new Set(node.children ?? []);
    const newChildPaths: string[] = [];
    // A child must be a DIRECT child of this node: its path extends the node's path by
    // exactly one segment. Anything else (the node itself, a sibling, a grandchild) means
    // an upstream chain was built across a gap — attaching it here would render the
    // subtree in a second, wrong location (and self-references loop traversals forever).
    const expectedParent = node.path ? `${node.path}/` : "";
    let rejectedCount = 0;
    for (const d of descendants) {
        const isDirectChild =
            d.path.length > expectedParent.length &&
            d.path.startsWith(expectedParent) &&
            !d.path.includes("/", expectedParent.length);
        if (!isDirectChild) {
            rejectedCount++;
            continue;
        }

        if (!childPaths.has(d.path)) {
            childPaths.add(d.path);
            newChildPaths.push(d.path);
        }
    }

    if (rejectedCount > 0) {
        // A rejection means some caller tried to attach a non-child — expected only when
        // the critical-path search came back with gaps (permission filtering, scan cap).
        // Surface it so bad chains are diagnosable instead of silently absorbed.
        telemetry.trackEvent("model_tree.non_direct_child_rejected", {
            "tree.parent_path_depth": node.path === "" ? 0 : node.path.split("/").length,
            "tree.rejected_count": rejectedCount,
            "tree.batch_size": descendants.length,
        });
    }

    return {
        ...node,
        children: [...(node.children ?? []), ...newChildPaths].sort((a, b) => childPathCollator.compare(a, b)),
        hasMoreChildren: Boolean(iterator),
        data: { ...node.data, childIterator: iterator },
    };
}

async function loadChildren({
    dataAccess,
    parent,
    tree,
    fileTypes,
    abortSignal,
    ensurePathFound,
    skipPdfFiles,
    knownPdfObjectIds,
}: {
    dataAccess: ObjectDataAccess;
    tree: Tree<ModelTreeNodeData>;
    /**
     * The folder to list, or `undefined` for the root tier — see {@link resolveParentRef}.
     */
    parent: HierarcicalObjectReference | undefined;
    fileTypes?: ModelTreeFilterObjects[];
    abortSignal: AbortSignal;
    /**
     * By default, we load first 500 children and the rest is loaded lazily.
     * But if we need to ensure some object (e.g. selected object) is loaded - we need to keep loading children
     * until we find that object.
     */
    ensurePathFound?: string;
    skipPdfFiles?: boolean;
    knownPdfObjectIds?: Pick<Set<ObjectId>, "has">;
}) {
    const path = parent?.path ?? "";
    const isFileOrInsideFile = getFilePathFromObjectPath(path);

    // Matched here rather than pushed into the query. The filter is a set of *this
    // folder's own child paths*, so testing it locally is exact, and it costs nothing on
    // the object tree, where no file-type filter is ever active. Only the standalone
    // Model Tree sets one, and only at or above file level, where sibling lists are short.
    const pathFilter =
        fileTypes && !isFileOrInsideFile ? new Set(getDescendantsFilteredByParserTypeInfo(path, fileTypes)) : undefined;

    const node: Node<ModelTreeNodeData> | undefined = tree[path];
    const iterator = node?.data.childIterator ?? dataAccess.getChildren(parent, abortSignal);
    const descendants: HierarcicalObjectReference[] = [];
    let done = false;
    let scanned = 0;
    const maxScan = 5000;
    // Budget the page by *distinct, not-yet-shown* child paths rather than raw records. The tree
    // is keyed by path, so duplicate-path records (common in some IFC/DWG exports) collapse to a
    // single row. Counting raw records here let a whole page dedupe down to nothing, so
    // "Load more" issued a search but added no visible rows and the button never cleared. Seeding
    // from the already-shown children means each call resumes past what the user can already see.
    const seenPaths = new Set<string>(node?.children ?? []);
    // Even if object is in the list of children - we consider it's not yet found.
    // It could be loaded individually, so by using ensurePathFound we want to load all the objects above it.
    let objectFound = false;
    while (descendants.length < 500 || (ensurePathFound && !objectFound)) {
        if (abortSignal.aborted) {
            break;
        }

        // Cap every scan, including selection-driven expansion. A selected child is injected
        // into the critical path separately by selectNode(), so continuing through an
        // unbounded sibling list merely to find it can retain millions of references and crash
        // the renderer on models with duplicated hierarchy paths.
        if (scanned >= maxScan) {
            break;
        }

        const next = await iterator.next();
        if (next.done) {
            done = true;
            break;
        }

        scanned++;
        const child = next.value;
        // An empty filter set means "nothing to narrow by", not "reject everything" —
        // the same as the empty `searchPattern` this replaced.
        if (pathFilter?.size && !pathFilter.has(child.path)) {
            // Same reason as the PDF skip below: a child the filter rejects can never be
            // appended, so an `ensurePathFound` target among them must count as found or
            // the loop scans to `maxScan` looking for something that will never arrive.
            if (ensurePathFound && !objectFound && ensurePathFound === child.path) {
                objectFound = true;
            }
            continue;
        }

        if (skipPdfFiles && !isFileOrInsideFile && (await isPdfOrDrawingObject(child, { knownPdfObjectIds }))) {
            // If the filtered-out child is the ensurePathFound target,
            // treat it as found so the loop doesn't scan unboundedly for
            // a target that can never be appended.
            if (ensurePathFound && !objectFound && ensurePathFound === child.path) {
                objectFound = true;
            }
            continue;
        }

        if (ensurePathFound && !objectFound && ensurePathFound === child.path) {
            objectFound = true;
        }

        // Skip paths already shown (or already seen earlier in this same scan) so the page fills
        // with genuinely new rows instead of collapsing to nothing in appendDescendants.
        if (seenPaths.has(child.path)) {
            continue;
        }
        seenPaths.add(child.path);
        descendants.push(child);
    }

    return { descendants, iterator: done ? undefined : iterator };
}

export const emptyModelTree: Tree<ModelTreeNodeData> = {
    "": {
        expanded: false,
        name: "Root", // Root name is never displayed, just for debugging
        isLeaf: false,
        path: "",
        loading: true,
        data: (() => {
            const object = {
                id: -1,
                path: "",
                name: "Root",
                properties: [],
                save: async () => false,
                type: NodeType.Internal,
                loadMetaData: async () => object,
            };
            return { object };
        })(),
    },
};

export function filterTypeToStr(filter: ModelTreeFilter) {
    switch (filter.type) {
        case ModelTreeFilterType.Terrain:
            return "terrain";
        case ModelTreeFilterType.ParserType:
            return `parser:${filter.parserType}`;
        default:
            throw new Error("Unknown filter type");
    }
}
